import { get, isEmpty } from 'lodash';
import { useFind, useFindRecord, usePopulate } from 'api/APIHooks';

export const useServiceAuthorizationData = (params, includeInternalNonConsentedClients) => {
  const personFields = [
    'first_name',
    'last_name',
    'date_of_birth',
    'phone_numbers',
    'consent',
  ].join(',');

  return useFindRecord(
    'service_authorization',
    params.id,
    {
      queryConfig: {
        placeholderData: undefined,
      },
      ...(includeInternalNonConsentedClients && {
        customParams: {
          include: 'person',
          fields: {
            person: personFields,
          },
        },
      }),
    },
  );
};

export const usePopulateQueries = (authorization, includeInternalNonConsentedClients) => {
  const fspQuery = usePopulate(
    'fee_schedule_program',
    'fee_schedule_program',
    authorization,
    { queryConfig: { placeholderData: undefined } },
  );
  const personConfig = { queryConfig: { enabled: !includeInternalNonConsentedClients, placeholderData: undefined } };
  const personQuery = usePopulate('person', 'person', authorization, personConfig);
  const caseQuery = usePopulate(
    'case',
    'case',
    authorization,
    { queryConfig: { placeholderData: undefined } },
  );
  const requesterQuery = usePopulate(
    'requester',
    'employee',
    authorization,
    { queryConfig: { placeholderData: undefined } },
  );
  const providerRequesterQuery = usePopulate(
    'provider',
    'provider',
    authorization.requester,
    { queryConfig: { placeholderData: undefined } },
  );
  const clinicalModification = usePopulate(
    'clinical_modifications',
    'clinical_modifications',
    authorization,
    { queryConfig: { placeholderData: undefined } },
  );

  // Use currently_approved_auth if auth is NOT approved, previously_approved_auth if approved
  // (if the current auth is approved, the previously_approved_auth is void)
  const isApproved = authorization.state === 'approved';
  const previousAuthKey = isApproved ? 'previously_approved_auth' : 'currently_approved_auth';

  const previousAuthInLineage = usePopulate(
    previousAuthKey,
    'service_authorization',
    authorization,
    { queryConfig: { placeholderData: undefined } },
  );

  const { data: previousAuthData } = previousAuthInLineage;
  const previousAuth = get(previousAuthData, 'data.data', {});

  const previousAuthClinicalModifications = usePopulate(
    'clinical_modifications',
    'clinical_modifications',
    previousAuth,
    { queryConfig: { placeholderData: undefined } },
  );

  usePopulate(
    'service_authorization_review_reason',
    'service_authorization_review_reason',
    authorization,
    { queryConfig: { placeholderData: undefined } },
  );

  return {
    fspQuery,
    personQuery,
    caseQuery,
    requesterQuery,
    providerRequesterQuery,
    clinicalModification,
    previousAuthInLineage,
    previousAuthClinicalModifications,
  };
};

export const useReferralQueries = (caseId) => {
  const referralsQuery = useFind(
    'referral',
    { case: caseId },
    { queryConfig: { enabled: Boolean(caseId), placeholderData: undefined } },
  );
  const { data: referralsData } = referralsQuery;
  const referrals = get(referralsData, 'data.data', {});

  const refQueryConfig = { queryConfig: { enabled: !isEmpty(referrals), placeholderData: undefined } };
  const recNetworkQuery = usePopulate('receiving_network', 'network', referrals, refQueryConfig);
  const recOrgQuery = usePopulate('receiving_provider', 'provider', referrals, refQueryConfig);
  const recProgramQuery = usePopulate('receiving_program', 'program', referrals, refQueryConfig);

  return {
    referralsQuery,
    referralsData,
    referrals,
    recNetworkQuery,
    recOrgQuery,
    recProgramQuery,
  };
};
